import type { AuthenticateData, RegisterData } from "@jti/evo-client";
import {
  authenticate,
  changeConsumerEmail,
  createEvoClient,
  dataSubjectAccessRequest,
  deactivateConsumer,
  deleteProfile,
  forgotPassword,
  getUsersPublicDomainConfiguration,
  register,
  setPassword,
  validateSession,
  verifyPhoneOrEmail,
} from "@jti/evo-client";
import type {
  DsarRequestPayload,
  Gender,
  LoginResponse,
  LoginVariables,
  PasswordRequirementsResponse,
  RegisterVariables,
  RegisterVariablesError,
  RegisterVariablesSuccess,
  ResetPasswordResponse,
  VerifyUpdateEmailResponse,
} from "@jti/fe-common";
import { DOB_INPUT_DATE, EMAIL_AUTH_STRATEGY, getConfig, ISO_DATE_FORMAT, parseCustomDate } from "@jti/fe-common";
import type { AuthenticationStrategiesConfig } from "@jti/rest-api-types";

import type { RestResponseType } from "../../../Rest";
import { getSessionTtl, handleAddPlusToPhone } from "../../utils";
import type { CasAuthApiContract, RegistrationPublicConfiguration, ResetPasswordRequest } from "../CasAuthApi.types";

export type ValidateSessionMutationResponse = ReturnType<typeof CasAuthApiEvo.prototype.validate>;

export class CasAuthApiEvo implements CasAuthApiContract {
  private readonly client;
  constructor(
    baseUrl: string,
    protected tenantToken: string,
    protected recaptchaId: string,
    protected ciamToken: string
  ) {
    this.client = createEvoClient({
      baseUrl,
      recaptchaId,
      tenantToken,
      ciamToken,
      throwOnError: true,
    });
  }

  // FIXME: Old CAS validate return 200 which is used in some places, new endpoint returns 204 now, which will break logic in those places
  async validate(ciamToken: string) {
    const { response } = await validateSession({
      client: this.client,
      body: {
        token: ciamToken,
      },
    });
    return { statusCode: response.status };
  }

  async login(variables: LoginVariables): Promise<RestResponseType<LoginResponse>> {
    const { defaultDesEvoLoginStrategy, overrideLoginSessionTtl } = getConfig();
    const strategy = variables.strategy ?? defaultDesEvoLoginStrategy ?? EMAIL_AUTH_STRATEGY;
    const { isKeepMeLoggedInEnabled, isKeepMeLoggedInChecked } = variables.extraData || {};

    const sessionTtl = getSessionTtl(isKeepMeLoggedInEnabled, overrideLoginSessionTtl, isKeepMeLoggedInChecked);

    const requestBody = {
      strategy,
      strategy_data: {
        identifier: variables.email,
        password: variables.password,
        ...variables.strategy_data,
      },
      session_ttl: sessionTtl,
    };

    const { data, response } = await authenticate({
      client: this.client,
      body: requestBody as AuthenticateData["body"],
    });

    const provinceName = data?.data?.profile?.data?.consumer?.custom_attributes?.province;

    return {
      uuid: data?.data?.user_uuid ?? "",
      ciam_token: data?.data?.token ?? "",
      magento_token: data?.data?.magento_token ?? undefined,
      statusCode: response.status,
      first_name: data?.data?.profile?.data?.consumer?.first_name,
      last_name: data?.data?.profile?.data?.consumer?.last_name,
      email: data?.data?.profile?.data?.consumer?.email,
      is_device_registered: data?.data?.profile?.data?.has_registered_device ?? undefined,
      is_age_verified: data?.data?.profile?.meta?.is_age_verified,
      ...(provinceName && { province_name: provinceName }),
    };
  }

  /**
   * Registration method
   * @param {RegisterVariables} variables
   * @returns {RegisterVariablesSuccess}
   * @throws {import("./EvoTypes").ValidationResponseError} 422 - Validation Response code
   * @throws {import("./EvoTypes").CommonResponseError} 400,409,424
   */

  async register(
    variables: RegisterVariables,
    dobFormat = DOB_INPUT_DATE
  ): Promise<RestResponseType<RegisterVariablesSuccess>> {
    const verifiedActionUuids: NonNullable<RegisterData["body"]>["verified_action_uuids"] = {};

    if (variables.phone_validation_uuid) {
      verifiedActionUuids["sms"] = variables.phone_validation_uuid;
    }

    if (variables.email_validation_uuid) {
      verifiedActionUuids["email"] = variables.email_validation_uuid;
    }

    const requestBody: RegisterData["body"] = {
      ...variables,
      // NOTE: Phone now is requiring to have + sign (e164 format)
      phone: variables.phone ? handleAddPlusToPhone(variables.phone) : "",
      first_name: variables.firstname,
      last_name: variables.lastname,
      verified_action_uuids: verifiedActionUuids,
      // NOTE: date_of_birth will be having ISO format YYYY-MM-DD (without time) now (partial ISO8601)
      date_of_birth: parseCustomDate({
        date: variables.date_of_birth,
        initialFormat: dobFormat,
        resultFormat: ISO_DATE_FORMAT,
      }),
      strategy: variables.strategy ?? EMAIL_AUTH_STRATEGY,
      strategy_data: {
        identifier: variables.email,
        password: variables.password,
        ...variables.strategy_data,
      },
    };

    const { data, response } = await register({
      client: this.client,
      body: requestBody,
    });

    return {
      statusCode: response.status,
      ...(!!data &&
        "authentication" in data && {
          authentication: {
            uuid: data.authentication.user_uuid,
            ciam_token: data.authentication.token,
            magento_token: data.authentication?.magento_token ?? undefined,
          },
        }),
    };
  }

  async registerCdpUser(): Promise<RestResponseType<RegisterVariablesSuccess | RegisterVariablesError>> {
    throw new Error("registerCdpUser is not supported in Evo");
  }

  async unregister(uuid?: string): Promise<unknown> {
    if (uuid === undefined) {
      return Promise.reject(new Error("Consumer ID is not defined"));
    }

    await deleteProfile({
      client: this.client,
    });
  }

  async deactivateAccount(uuid?: string): Promise<void> {
    if (uuid === undefined) {
      return Promise.reject(new Error("Consumer ID is not defined"));
    }

    await deactivateConsumer({
      client: this.client,
      path: {
        uuid,
      },
    });
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    const { data } = await forgotPassword({
      client: this.client,
      body: {
        email,
      },
    });

    return { message: data?.message ?? "" };
  }

  async resetConsumerPassword({
    newPassword,
    ciamToken,
    confirmPassword,
  }: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    const { data } = await setPassword({
      client: this.client,
      path: {
        token: ciamToken,
      },
      body: {
        password: newPassword,
        password_confirmation: confirmPassword,
      },
    });

    return { message: data?.message ?? "" };
  }

  async verifyEmailUpdate(uuidOfRequest: string): Promise<VerifyUpdateEmailResponse> {
    const { data, response } = await changeConsumerEmail({
      client: this.client,
      body: {
        uuid: uuidOfRequest,
      },
    });

    const verifyEmailResponse = {
      personal_info: {
        firstname: data?.first_name ?? "",
        lastname: data?.last_name ?? "",
        date_of_birth: data?.date_of_birth ?? "",
        email: data?.email ?? "",
        gender: data?.gender ? (data?.gender as Gender) : undefined,
        phone: data?.phone ?? "",
      },
      addresses: data?.addresses ?? "",
      statusCode: response.status ?? "",
    } as VerifyUpdateEmailResponse;

    return verifyEmailResponse;
  }

  async verifyConsumer(email: string): Promise<{ statusCode: string }> {
    const { response, error } = await verifyPhoneOrEmail({
      client: this.client,
      path: {
        phone_or_email: email,
      },
      throwOnError: false,
    });

    if (error) {
      throw { ...error, statusCode: String(response?.status ?? "") };
    }

    return { statusCode: String(response?.status ?? "") };
  }

  async getPasswordRequirements(): Promise<RestResponseType<PasswordRequirementsResponse>> {
    const { data, response } = await getUsersPublicDomainConfiguration({ client: this.client });

    return {
      statusCode: response.status,
      minLength: data?.password_requirements.min_length ?? 0,
      minLowerCase: data?.password_requirements.min_lower_case ?? 0,
      minUpperCase: data?.password_requirements.min_upper_case ?? 0,
      minNumber: data?.password_requirements.min_number ?? 0,
      minSymbol: data?.password_requirements.min_symbol ?? 0,
      excludeUsername: data?.password_requirements.exclude_username ?? false,
    };
  }

  async getRegistrationPublicConfiguration(): Promise<RestResponseType<RegistrationPublicConfiguration>> {
    const { data, response } = await getUsersPublicDomainConfiguration({ client: this.client });

    return {
      statusCode: response.status,
      requiresDeviceRegistration: data?.registration?.requires_device_registration ?? false,
    };
  }

  async dsarRequest(variables: DsarRequestPayload): Promise<RestResponseType> {
    const requestBody = {
      first_name: variables.firstName,
      last_name: variables.lastName,
      email: variables.email,
      language: variables.language,
      request_types: variables.requestTypes,
      market: variables.market,
      country: variables.country,
    };

    const { response } = await dataSubjectAccessRequest({
      client: this.client,
      body: requestBody,
    });

    return { statusCode: response.status };
  }

  async getAvailableAuthMethods(): Promise<
    RestResponseType<{ availableAuthMethods?: AuthenticationStrategiesConfig }>
  > {
    const { data, response } = await getUsersPublicDomainConfiguration({ client: this.client });

    return { availableAuthMethods: data?.available_authentication_strategies, statusCode: response.status };
  }
}
